/**
 * AdminMemoTeamDetailPage — admin-only full memo history for one
 * memo team. Reachable from `/admin/memos` → click a team.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Eye, Users as UsersIcon } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { usePageHeader } from '../context/PageHeaderContext';

const initials = (n) => ((n || '').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?');

const AdminMemoTeamDetailPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [membersOpen, setMembersOpen] = useState(false);

  usePageHeader({
    title: data?.team?.name || 'Team Memos',
    back: () => navigate('/admin/memos'),
    rightSlot: data ? (
      <button
        onClick={() => setMembersOpen(true)}
        className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center"
        aria-label="View team members"
        data-testid="memos-header-eye"
      >
        <Eye className="w-5 h-5" />
      </button>
    ) : null,
  }, [navigate, data]);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    if (!isAdmin || !teamId) return;
    try { const r = await api.get(`/memos/admin/teams/${teamId}`); setData(r.data); }
    catch (e) { setData({ error: e?.response?.data?.detail || 'Failed to load' }); }
  }, [api, teamId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return <div className="min-h-screen flex items-center justify-center"><p className="text-sm text-muted-foreground">Admin only.</p></div>;
  if (data === null) return <PageLoader label="Loading memos..." />;
  if (data?.error) return <div className="min-h-screen flex items-center justify-center"><p className="text-sm text-red-600">{data.error}</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" data-testid="admin-memo-team-detail">
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <Card className="rounded-2xl">
          <CardContent className="p-4 flex items-center gap-3">
            <UsersIcon className="w-6 h-6 text-[#586D52]" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{data.team?.name}</p>
              <p className="text-xs text-muted-foreground">{(data.members || []).length} member{data.members?.length === 1 ? '' : 's'} · {(data.memos || []).length} memo{data.memos?.length === 1 ? '' : 's'}</p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2" data-testid="admin-memo-team-memos">
          {(data.memos || []).length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No memos posted in this team yet.</p>
          ) : (data.memos || []).map((m) => (
            <div key={m.id} className="p-3 rounded-2xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs font-semibold text-[#586D52]">{m.owner_name}</p>
                <p className="text-[11px] text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
              </div>
              <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-md max-h-[80vh] overflow-y-auto" data-testid="admin-memo-team-members-dialog">
          <DialogTitle className="text-base font-semibold">{data.team?.name} · members</DialogTitle>
          <div className="space-y-1.5 mt-2">
            {(data.members || []).length === 0
              ? <p className="text-sm text-muted-foreground text-center py-4">No members.</p>
              : data.members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    {m.profile_picture ? <AvatarImage src={m.profile_picture} alt="" /> : null}
                    <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] font-semibold text-sm">{initials(m.name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{m.name}</p>
                    <p className="text-[11px] text-muted-foreground capitalize truncate">
                      {[m.role, m.industry, m.industry_domain].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
              ))
            }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMemoTeamDetailPage;
